import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  .land-root {
    font-family: 'DM Sans', sans-serif;
    color: #0f1117;
    background: #ffffff;
    overflow-x: hidden;
  }

  /* ── Navbar ── */
  .land-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 999;
    padding: 0 32px;
    height: 68px;
    display: flex; align-items: center; justify-content: space-between;
    transition: all 0.3s ease;
  }
  .land-nav.scrolled {
    background: rgba(255,255,255,0.92);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(0,0,0,0.08);
    box-shadow: 0 2px 20px rgba(0,0,0,0.06);
  }
  .land-nav-logo {
    font-family: 'Syne', sans-serif;
    font-size: 22px; font-weight: 800;
    color: #1a5c2a;
    text-decoration: none;
    display: flex; align-items: center; gap: 10px;
    letter-spacing: -0.3px;
  }
  .land-logo-icon {
    width: 36px; height: 36px;
    background: #1a5c2a;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px;
  }
  .land-nav-links {
    display: flex; align-items: center; gap: 8px;
  }
  .land-nav-link {
    font-size: 14px; font-weight: 500; color: #444;
    text-decoration: none; padding: 8px 16px; border-radius: 8px;
    transition: all 0.2s;
  }
  .land-nav-link:hover { background: #f5f5f5; color: #000; }
  .land-btn-signin {
    font-size: 14px; font-weight: 600; color: #1a5c2a;
    text-decoration: none; padding: 9px 20px;
    border: 1.5px solid #1a5c2a; border-radius: 10px;
    transition: all 0.2s;
  }
  .land-btn-signin:hover { background: #1a5c2a; color: white; }
  .land-btn-start {
    font-size: 14px; font-weight: 600; color: white;
    text-decoration: none; padding: 9px 20px;
    background: #1a5c2a; border-radius: 10px;
    transition: all 0.2s; box-shadow: 0 2px 8px rgba(26,92,42,0.35);
  }
  .land-btn-start:hover { background: #145022; transform: translateY(-1px); box-shadow: 0 4px 16px rgba(26,92,42,0.4); }

  /* ── Mobile hamburger ── */
  .land-hamburger {
    display: none;
    flex-direction: column; gap: 5px; cursor: pointer;
    background: none; border: none; padding: 4px;
  }
  .land-hamburger span {
    display: block; width: 24px; height: 2px;
    background: #1a5c2a; border-radius: 2px; transition: all 0.3s;
  }
  .land-mobile-menu {
    display: none;
    position: fixed; top: 68px; left: 0; right: 0;
    background: white; border-bottom: 1px solid rgba(0,0,0,0.1);
    padding: 16px 24px; flex-direction: column; gap: 8px;
    z-index: 998; box-shadow: 0 8px 24px rgba(0,0,0,0.1);
  }
  .land-mobile-menu.open { display: flex; }
  .land-mobile-link {
    font-size: 15px; font-weight: 500; color: #333;
    text-decoration: none; padding: 10px 0;
    border-bottom: 1px solid #f0f0f0;
  }
  .land-mobile-link:last-child { border-bottom: none; }

  /* ── Hero ── */
  .land-hero {
    min-height: 100vh;
    background: linear-gradient(135deg, #0a1f0f 0%, #1a5c2a 50%, #0d3318 100%);
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    text-align: center;
    padding: 120px 24px 80px;
    position: relative; overflow: hidden;
  }
  .land-hero::before {
    content: '';
    position: absolute; inset: 0;
    background: radial-gradient(ellipse 80% 60% at 50% 40%, rgba(255,255,255,0.06) 0%, transparent 70%);
    pointer-events: none;
  }
  .land-hero::after {
    content: '';
    position: absolute; inset: 0;
    background-image: radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px);
    background-size: 32px 32px;
    pointer-events: none; opacity: 0.5;
  }
  .land-hero-badge {
    display: inline-flex; align-items: center; gap: 8px;
    background: rgba(255,255,255,0.1);
    border: 1px solid rgba(255,255,255,0.2);
    color: rgba(255,255,255,0.9);
    font-size: 13px; font-weight: 500;
    padding: 6px 16px; border-radius: 50px;
    margin-bottom: 28px;
    backdrop-filter: blur(8px);
    animation: fadeUp 0.6s ease both;
  }
  .land-hero h1 {
    font-family: 'Syne', sans-serif;
    font-size: clamp(42px, 7vw, 82px);
    font-weight: 800; color: white;
    line-height: 1.05; letter-spacing: -2px;
    max-width: 900px; margin-bottom: 24px;
    animation: fadeUp 0.6s ease 0.1s both;
  }
  .land-hero h1 span {
    background: linear-gradient(90deg, #a3f0b5, #4ade80);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
  .land-hero-sub {
    font-size: clamp(16px, 2vw, 20px);
    color: rgba(255,255,255,0.72);
    max-width: 560px; line-height: 1.65;
    margin-bottom: 40px; font-weight: 400;
    animation: fadeUp 0.6s ease 0.2s both;
  }
  .land-hero-cta {
    display: flex; gap: 14px; justify-content: center; flex-wrap: wrap;
    animation: fadeUp 0.6s ease 0.3s both;
  }
  .land-cta-primary {
    background: white; color: #1a5c2a;
    font-family: 'DM Sans', sans-serif;
    font-size: 16px; font-weight: 700;
    text-decoration: none;
    padding: 14px 32px; border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.25);
    transition: all 0.2s;
    display: inline-flex; align-items: center; gap: 8px;
  }
  .land-cta-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(0,0,0,0.35); }
  .land-cta-secondary {
    background: rgba(255,255,255,0.1);
    border: 1.5px solid rgba(255,255,255,0.3);
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 16px; font-weight: 600;
    text-decoration: none;
    padding: 14px 32px; border-radius: 12px;
    backdrop-filter: blur(8px); transition: all 0.2s;
  }
  .land-cta-secondary:hover { background: rgba(255,255,255,0.2); }

  /* ── Preview card ── */
  .land-preview {
    margin-top: 64px; width: 100%; max-width: 860px;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 20px; padding: 20px;
    backdrop-filter: blur(8px);
    animation: fadeUp 0.8s ease 0.4s both;
  }
  .land-preview-bar { display: flex; gap: 6px; margin-bottom: 14px; }
  .land-preview-dot { width: 12px; height: 12px; border-radius: 50%; }
  .land-preview-inner {
    background: #f8faf8; border-radius: 12px;
    padding: 20px 24px;
    display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px;
  }
  .land-preview-stat {
    background: white; border-radius: 10px;
    padding: 16px; border: 1px solid #e8f5e9; text-align: center;
  }
  .land-preview-stat-val { font-family: 'Syne', sans-serif; font-size: 28px; font-weight: 800; color: #1a5c2a; }
  .land-preview-stat-label { font-size: 12px; color: #888; margin-top: 4px; }
  .land-preview-menu { grid-column: 1 / -1; display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .land-preview-item {
    background: white; border: 1px solid #e8f5e9;
    border-radius: 10px; padding: 12px 16px;
    display: flex; align-items: center; justify-content: space-between;
  }
  .land-preview-item-name { font-size: 13px; font-weight: 600; color: #222; }
  .land-preview-item-price { font-size: 13px; font-weight: 700; color: #1a5c2a; }
  .land-preview-item-badge {
    font-size: 10px; font-weight: 600;
    padding: 2px 8px; border-radius: 50px;
    margin-top: 2px; display: inline-block;
  }

  /* ── Section ── */
  .land-section { padding: 96px 24px; max-width: 1100px; margin: 0 auto; }
  .land-section-tag {
    font-size: 12px; font-weight: 700; color: #1a5c2a;
    letter-spacing: 2px; text-transform: uppercase; margin-bottom: 12px;
  }
  .land-section-title {
    font-family: 'Syne', sans-serif;
    font-size: clamp(32px, 4vw, 48px); font-weight: 800;
    letter-spacing: -1.5px; color: #0f1117;
    line-height: 1.1; max-width: 600px; margin-bottom: 16px;
  }
  .land-section-sub { font-size: 17px; color: #666; line-height: 1.7; max-width: 520px; margin-bottom: 56px; }

  /* ── Features grid ── */
  .land-features { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  .land-feature-card {
    background: #f8faf8; border: 1px solid #e8f0e8;
    border-radius: 18px; padding: 28px;
    transition: all 0.25s; cursor: default;
  }
  .land-feature-card:hover {
    background: white; border-color: #1a5c2a;
    transform: translateY(-4px); box-shadow: 0 12px 40px rgba(26,92,42,0.12);
  }
  .land-feature-icon {
    width: 48px; height: 48px; background: #e8f5e9;
    border-radius: 12px; display: flex; align-items: center;
    justify-content: center; font-size: 22px; margin-bottom: 18px;
  }
  .land-feature-title { font-family: 'Syne', sans-serif; font-size: 18px; font-weight: 700; margin-bottom: 8px; color: #0f1117; }
  .land-feature-desc { font-size: 14px; color: #666; line-height: 1.65; }

  /* ── How it works ── */
  .land-hiw { background: #f8faf8; }
  .land-hiw-inner { max-width: 1100px; margin: 0 auto; padding: 96px 24px; }
  .land-steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; position: relative; }
  .land-steps::before {
    content: ''; position: absolute;
    top: 32px; left: calc(16.66% + 16px); right: calc(16.66% + 16px);
    height: 2px; background: linear-gradient(90deg, #1a5c2a, #4ade80, #1a5c2a); z-index: 0;
  }
  .land-step { text-align: center; position: relative; z-index: 1; }
  .land-step-num {
    width: 64px; height: 64px; background: #1a5c2a;
    border-radius: 50%; display: flex; align-items: center; justify-content: center;
    font-family: 'Syne', sans-serif; font-size: 24px; font-weight: 800; color: white;
    margin: 0 auto 20px; box-shadow: 0 4px 20px rgba(26,92,42,0.35);
  }
  .land-step-title { font-family: 'Syne', sans-serif; font-size: 18px; font-weight: 700; margin-bottom: 10px; }
  .land-step-desc { font-size: 14px; color: #666; line-height: 1.65; max-width: 240px; margin: 0 auto; }

  /* ── Pricing ── */
  .land-pricing-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; max-width: 760px; margin: 0 auto; }
  .land-price-card { border: 1.5px solid #e0e0e0; border-radius: 20px; padding: 32px; background: white; transition: all 0.25s; }
  .land-price-card.featured {
    border-color: #1a5c2a; background: #0f1117; color: white;
    box-shadow: 0 8px 40px rgba(26,92,42,0.3); transform: scale(1.03);
  }
  .land-price-badge {
    font-size: 11px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase;
    background: #4ade80; color: #0f1117; padding: 4px 12px; border-radius: 50px;
    display: inline-block; margin-bottom: 16px;
  }
  .land-price-name { font-family: 'Syne', sans-serif; font-size: 22px; font-weight: 800; margin-bottom: 8px; }
  .land-price-amount { font-family: 'Syne', sans-serif; font-size: 48px; font-weight: 800; line-height: 1; margin-bottom: 4px; }
  .land-price-period { font-size: 14px; opacity: 0.6; margin-bottom: 24px; }
  .land-price-features { list-style: none; margin-bottom: 28px; }
  .land-price-features li { font-size: 14px; padding: 7px 0; border-bottom: 1px solid rgba(0,0,0,0.06); display: flex; align-items: center; gap: 10px; }
  .land-price-card.featured .land-price-features li { border-bottom-color: rgba(255,255,255,0.08); }
  .land-price-btn {
    width: 100%; padding: 13px; border-radius: 12px;
    font-family: 'DM Sans', sans-serif; font-size: 15px; font-weight: 700;
    text-align: center; text-decoration: none; display: block;
    transition: all 0.2s; border: none; cursor: pointer;
  }
  .land-price-btn.outline { border: 1.5px solid #e0e0e0; color: #333; background: white; }
  .land-price-btn.outline:hover { border-color: #1a5c2a; color: #1a5c2a; }
  .land-price-btn.filled { background: #4ade80; color: #0f1117; box-shadow: 0 4px 16px rgba(74,222,128,0.4); }
  .land-price-btn.filled:hover { background: #22c55e; transform: translateY(-1px); }

  /* ── FAQ ── */
  .land-faq { background: white; }
  .land-faq-inner { max-width: 720px; margin: 0 auto; padding: 96px 24px; }
  .land-faq-item {
    border: 1px solid #e8f0e8; border-radius: 12px;
    margin-bottom: 12px; overflow: hidden;
    transition: all 0.2s;
  }
  .land-faq-item.open { border-color: #1a5c2a; }
  .land-faq-q {
    padding: 18px 20px; display: flex; justify-content: space-between;
    align-items: center; cursor: pointer; font-weight: 600; font-size: 15px;
    background: white; gap: 16px;
  }
  .land-faq-item.open .land-faq-q { background: #f0fdf4; color: #1a5c2a; }
  .land-faq-icon { font-size: 20px; flex-shrink: 0; color: #1a5c2a; transition: transform 0.2s; }
  .land-faq-item.open .land-faq-icon { transform: rotate(45deg); }
  .land-faq-a { padding: 0 20px 18px; font-size: 14px; color: #555; line-height: 1.75; display: none; }
  .land-faq-item.open .land-faq-a { display: block; }

  /* ── CTA section ── */
  .land-cta-section {
    background: linear-gradient(135deg, #0a1f0f, #1a5c2a);
    padding: 96px 24px; text-align: center;
    position: relative; overflow: hidden;
  }
  .land-cta-section::after {
    content: ''; position: absolute; inset: 0;
    background-image: radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px);
    background-size: 28px 28px; pointer-events: none;
  }
  .land-cta-title {
    font-family: 'Syne', sans-serif;
    font-size: clamp(32px, 5vw, 56px); font-weight: 800; color: white;
    letter-spacing: -1.5px; margin-bottom: 16px; position: relative; z-index: 1;
  }
  .land-cta-sub { font-size: 18px; color: rgba(255,255,255,0.65); margin-bottom: 40px; position: relative; z-index: 1; }

  /* ── Footer ── */
  .land-footer { background: #0f1117; padding: 48px 32px 32px; }
  .land-footer-inner {
    max-width: 1100px; margin: 0 auto;
    display: flex; justify-content: space-between;
    align-items: flex-start; flex-wrap: wrap; gap: 32px;
    padding-bottom: 32px; border-bottom: 1px solid rgba(255,255,255,0.08);
  }
  .land-footer-brand {
    font-family: 'Syne', sans-serif; font-size: 20px; font-weight: 800; color: white;
    display: flex; align-items: center; gap: 10px; margin-bottom: 10px;
  }
  .land-footer-tagline { font-size: 13px; color: rgba(255,255,255,0.4); max-width: 220px; }
  .land-footer-col-title { font-size: 13px; font-weight: 700; color: rgba(255,255,255,0.5); letter-spacing: 1px; text-transform: uppercase; margin-bottom: 16px; }
  .land-footer-link { display: block; font-size: 14px; color: rgba(255,255,255,0.6); text-decoration: none; margin-bottom: 10px; transition: color 0.2s; }
  .land-footer-link:hover { color: white; }
  .land-footer-bottom {
    max-width: 1100px; margin: 24px auto 0;
    display: flex; justify-content: space-between; align-items: center;
    flex-wrap: wrap; gap: 12px;
    font-size: 13px; color: rgba(255,255,255,0.3);
  }

  /* ── Animations ── */
  @keyframes fadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }

  /* ── Mobile ── */
  @media (max-width: 768px) {
    .land-features { grid-template-columns: 1fr; }
    .land-steps { grid-template-columns: 1fr; }
    .land-steps::before { display: none; }
    .land-pricing-grid { grid-template-columns: 1fr; }
    .land-price-card.featured { transform: none; }
    .land-footer-inner { flex-direction: column; }
    .land-preview-inner { grid-template-columns: 1fr; }
    .land-preview-menu { grid-template-columns: 1fr; }
    .land-nav { padding: 0 16px; }
    .land-nav-links { display: none; }
    .land-hamburger { display: flex; }
  }
  
  /* ── Live Demo Section ── */
.land-demo {
  background: #0f1117;
  padding: 96px 24px;
}
.land-demo-inner {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
}
.land-demo-tag {
  font-size: 12px; font-weight: 700;
  color: #4ade80; letter-spacing: 2px;
  text-transform: uppercase; margin-bottom: 12px;
}
.land-demo-title {
  font-family: 'Syne', sans-serif;
  font-size: clamp(28px, 3.5vw, 42px);
  font-weight: 800; color: white;
  letter-spacing: -1px; line-height: 1.1;
  margin-bottom: 16px;
}
.land-demo-sub {
  font-size: 16px; color: rgba(255,255,255,0.55);
  line-height: 1.7; margin-bottom: 32px;
}
.land-demo-pills {
  display: flex; flex-direction: column; gap: 12px;
  margin-bottom: 36px;
}
.land-demo-pill {
  display: flex; align-items: flex-start; gap: 12px;
}
.land-demo-pill-icon {
  width: 32px; height: 32px; flex-shrink: 0;
  background: rgba(74,222,128,0.12);
  border: 1px solid rgba(74,222,128,0.2);
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  font-size: 15px;
}
.land-demo-pill-text { font-size: 14px; color: rgba(255,255,255,0.75); line-height: 1.55; padding-top: 6px; }
.land-demo-pill-text strong { color: white; }
.land-demo-view-btn {
  display: inline-flex; align-items: center; gap: 8px;
  background: #4ade80; color: #0f1117;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px; font-weight: 700;
  padding: 13px 28px; border-radius: 12px;
  text-decoration: none;
  box-shadow: 0 4px 20px rgba(74,222,128,0.3);
  transition: all 0.2s;
}
.land-demo-view-btn:hover {
  background: #22c55e;
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(74,222,128,0.4);
}
.land-demo-right {
  display: flex; flex-direction: column;
  align-items: center; gap: 0;
}
.land-demo-qr-card {
  background: white;
  border-radius: 24px;
  padding: 32px;
  display: flex; flex-direction: column;
  align-items: center; gap: 20px;
  box-shadow: 0 24px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06);
  position: relative;
}
.land-demo-qr-label {
  font-size: 13px; font-weight: 700;
  color: #1a5c2a; letter-spacing: 0.5px;
  text-align: center;
}
.land-demo-qr-restaurant {
  font-family: 'Syne', sans-serif;
  font-size: 17px; font-weight: 800;
  color: #0f1117; text-align: center;
  margin-top: -8px; line-height: 1.2;
}
.land-demo-qr-cuisine {
  font-size: 12px; color: #888;
  text-align: center; margin-top: -12px;
}
.land-demo-qr-divider {
  width: 100%; height: 1px;
  background: #f0f0f0;
}
.land-demo-features-row {
  display: flex; gap: 16px;
}
.land-demo-feat {
  display: flex; flex-direction: column;
  align-items: center; gap: 4px;
  font-size: 11px; color: #888; text-align: center;
}
.land-demo-feat-icon {
  font-size: 18px;
}
.land-demo-mobile-hint {
  margin-top: 16px;
  font-size: 12px; color: rgba(255,255,255,0.3);
  text-align: center; letter-spacing: 0.3px;
}
.land-demo-live-dot {
  display: inline-flex; align-items: center; gap: 6px;
  background: rgba(74,222,128,0.1);
  border: 1px solid rgba(74,222,128,0.25);
  border-radius: 50px; padding: 4px 12px;
  font-size: 12px; font-weight: 600; color: #4ade80;
  margin-bottom: 20px;
}
.land-demo-live-dot::before {
  content: '';
  width: 7px; height: 7px;
  background: #4ade80; border-radius: 50%;
  animation: pulse-green 1.8s ease infinite;
}
@keyframes pulse-green {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(0.8); }
}
@media (max-width: 768px) {
  .land-demo-inner {
    grid-template-columns: 1fr;
    gap: 48px;
  }
  .land-demo-right { order: -1; }
}
`;

const CUISINES = [
  {
    label: '🇮🇳 Indian', color: '#ff9500',
    items: [
      { name: 'Chicken Biryani', price: '$16.99', tag: 'non-veg', tagColor: '#fde8e8', tagText: '#c0392b', img: 'https://images.unsplash.com/photo-1589302168068-964664d93dc0?w=80&h=80&fit=crop' },
      { name: 'Paneer Tikka', price: '$13.99', tag: 'veg', tagColor: '#e8f5e9', tagText: '#1a5c2a', img: 'https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?w=80&h=80&fit=crop' },
      { name: 'Butter Naan', price: '$3.99', tag: 'veg', tagColor: '#e8f5e9', tagText: '#1a5c2a', img: 'https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=80&h=80&fit=crop' },
      { name: 'Lamb Kebab', price: '$18.99', tag: 'halal', tagColor: '#e8f0ff', tagText: '#3730a3', img: 'https://images.unsplash.com/photo-1599487488170-d11ec9c172f0?w=80&h=80&fit=crop' },
    ]
  },
  {
    label: '🇨🇳 Chinese', color: '#ef4444',
    items: [
      { name: 'Kung Pao Chicken', price: '$14.99', tag: 'non-veg', tagColor: '#fde8e8', tagText: '#c0392b', img: 'https://images.unsplash.com/photo-1525755662778-989d0524087e?w=80&h=80&fit=crop' },
      { name: 'Vegetable Dim Sum', price: '$10.99', tag: 'veg', tagColor: '#e8f5e9', tagText: '#1a5c2a', img: 'https://images.unsplash.com/photo-1563245372-f21724e3856d?w=80&h=80&fit=crop' },
      { name: 'Fried Rice', price: '$11.99', tag: 'veg', tagColor: '#e8f5e9', tagText: '#1a5c2a', img: 'https://images.unsplash.com/photo-1603133872878-684f208fb84b?w=80&h=80&fit=crop' },
      { name: 'Spring Rolls', price: '$8.99', tag: 'vegan', tagColor: '#f0fdf4', tagText: '#166534', img: 'https://images.unsplash.com/photo-1606335543042-57c525922933?w=80&h=80&fit=crop' },
    ]
  },
  {
    label: '🇲🇽 Mexican', color: '#f59e0b',
    items: [
      { name: 'Tacos Al Pastor', price: '$12.99', tag: 'non-veg', tagColor: '#fde8e8', tagText: '#c0392b', img: 'https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=80&h=80&fit=crop' },
      { name: 'Guacamole & Chips', price: '$7.99', tag: 'vegan', tagColor: '#f0fdf4', tagText: '#166534', img: 'https://images.unsplash.com/photo-1541014741259-de529411b96a?w=80&h=80&fit=crop' },
      { name: 'Burrito Bowl', price: '$13.99', tag: 'non-veg', tagColor: '#fde8e8', tagText: '#c0392b', img: 'https://images.unsplash.com/photo-1626700051175-6818013e1d4f?w=80&h=80&fit=crop' },
      { name: 'Cheese Quesadilla', price: '$9.99', tag: 'veg', tagColor: '#e8f5e9', tagText: '#1a5c2a', img: 'https://images.unsplash.com/photo-1618040996337-56904b7850b9?w=80&h=80&fit=crop' },
    ]
  },
  {
    label: '🇮🇹 Italian', color: '#10b981',
    items: [
      { name: 'Margherita Pizza', price: '$15.99', tag: 'veg', tagColor: '#e8f5e9', tagText: '#1a5c2a', img: 'https://images.unsplash.com/photo-1574071318508-1cdbab80d002?w=80&h=80&fit=crop' },
      { name: 'Spaghetti Carbonara', price: '$16.99', tag: 'non-veg', tagColor: '#fde8e8', tagText: '#c0392b', img: 'https://images.unsplash.com/photo-1612874742237-6526221588e3?w=80&h=80&fit=crop' },
      { name: 'Tiramisu', price: '$7.99', tag: 'veg', tagColor: '#e8f5e9', tagText: '#1a5c2a', img: 'https://images.unsplash.com/photo-1571877227200-a0d98ea607e9?w=80&h=80&fit=crop' },
      { name: 'Mushroom Risotto', price: '$14.99', tag: 'vegan', tagColor: '#f0fdf4', tagText: '#166534', img: 'https://images.unsplash.com/photo-1476124369491-e7addf5db371?w=80&h=80&fit=crop' },
    ]
  },
  {
    label: '🇯🇵 Japanese', color: '#8b5cf6',
    items: [
      { name: 'Sushi Platter', price: '$22.99', tag: 'non-veg', tagColor: '#fde8e8', tagText: '#c0392b', img: 'https://images.unsplash.com/photo-1553621042-f6e147245754?w=80&h=80&fit=crop' },
      { name: 'Ramen Bowl', price: '$14.99', tag: 'non-veg', tagColor: '#fde8e8', tagText: '#c0392b', img: 'https://images.unsplash.com/photo-1569050467447-ce54b3bbc37d?w=80&h=80&fit=crop' },
      { name: 'Vegetable Tempura', price: '$11.99', tag: 'veg', tagColor: '#e8f5e9', tagText: '#1a5c2a', img: 'https://images.unsplash.com/photo-1569050467447-ce54b3bbc37d?w=80&h=80&fit=crop' },
      { name: 'Mochi Ice Cream', price: '$6.99', tag: 'veg', tagColor: '#e8f5e9', tagText: '#1a5c2a', img: 'https://images.unsplash.com/photo-1563805042-7684c019e1cb?w=80&h=80&fit=crop' },
    ]
  },
];

const FEATURES = [
  { icon: '🍽️', title: 'Digital Menu Builder', desc: 'Create stunning menus with categories, photos, prices, dietary tags and spice levels. Update instantly — no reprinting ever.' },
  { icon: '📱', title: 'QR Code Generator', desc: 'Auto-generate scannable QR codes for every restaurant. Download, print, and place on tables in seconds.' },
  { icon: '🛒', title: 'Online Ordering', desc: 'Let customers browse, add to cart and place orders directly from the menu. Real-time order management for your staff.' },
  { icon: '📧', title: 'Auto Email Confirmations', desc: 'Customers receive branded order confirmations with prep time automatically via email — zero manual effort.' },
  { icon: '🔔', title: 'Instant Order Alerts', desc: 'Sound notifications and live banners alert your front desk the moment a new order arrives. Never miss an order.' },
  { icon: '🏪', title: 'Multi-Restaurant Support', desc: 'Manage multiple restaurant brands from one account. Each restaurant gets its own menu, QR code, and orders dashboard.' },
];

const STEPS = [
  { num: '1', title: 'Create Your Menu', desc: 'Sign up, add your restaurant details, upload photos, and build your menu with categories and items.' },
  { num: '2', title: 'Generate QR Code', desc: 'Get your unique QR code instantly. Print it and place it on tables, counters or packaging.' },
  { num: '3', title: 'Receive Orders', desc: 'Customers scan, browse and order. You get notified instantly and manage everything from one dashboard.' },
];

const FAQS = [
  {
    q: 'Do I need internet at my restaurant for this to work?',
    a: 'Yes — your customers need internet to scan the QR code and view the menu. Any standard Wi-Fi or mobile data works perfectly. If your restaurant already has Wi-Fi for customers, you are all set.',
  },
  {
    q: 'Can I update my menu myself anytime?',
    a: 'Absolutely. You can add, edit, or remove menu items instantly from any device — phone, tablet, or computer. Changes go live on your public menu immediately. No technical knowledge needed.',
  },
  {
    q: 'What happens if I cancel my subscription?',
    a: 'You can cancel anytime with no penalty. Your menu stays active until the end of your current billing period. After that, your account is paused. Your data is retained for 30 days in case you want to reactivate.',
  },
  {
    q: 'Is there a limit on how many menu items I can add?',
    a: 'No limit on either plan. Add as many categories, items, photos, and descriptions as you need. Both Basic and Pro support unlimited menu items.',
  },
  {
    q: 'How does online ordering work for my customers?',
    a: 'On the Pro plan, customers scan your QR code, browse your menu, add items to their cart, and place an order with their name and contact details. You get a sound notification instantly on your dashboard. No third-party app needed — customers pay you directly in person at pickup.',
  },
  {
    q: 'What is the difference between Basic and Pro?',
    a: 'Basic gives you a full digital menu with QR code, photos, dietary tags, 10-language support, and analytics. Pro adds online ordering so customers can place orders directly from the menu, catering request management, automatic order confirmation emails, and priority support.',
  },
  {
    q: 'Is my payment information secure?',
    a: 'Yes. All payments are processed by Lemon Squeezy, which is powered by Stripe — one of the most trusted payment processors in the world. We never store your card details. All transactions are encrypted.',
  },
];

export default function Landing() {
  const [scrolled, setScrolled] = useState(false);
  const [cuisineIdx, setCuisineIdx] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const [pricingPeriod, setPricingPeriod] = useState('monthly');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [liveStats, setLiveStats] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/stats`)
      .then(r => r.json()).then(setLiveStats).catch(() => {});
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setCuisineIdx(prev => (prev + 1) % CUISINES.length);
        setFadeIn(true);
      }, 300);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const currentCuisine = CUISINES[cuisineIdx];

  // Only show stats bar if we have real data
  const showStats = liveStats !== null;

  return (
    <div className="land-root">
      <style>{styles}</style>

      {/* ── Navbar ── */}
      <nav className={`land-nav ${scrolled ? 'scrolled' : ''}`}>
        <Link to="/" className="land-nav-logo">
          <img src={require('../assets/logo.png')} alt="QR Digi Menu" style={{ width: 36, height: 36, borderRadius: 10, objectFit: 'cover' }} />
          QR Digi Menu
        </Link>
        <div className="land-nav-links">
          <a href="#features" className="land-nav-link">Features</a>
          <a href="#how-it-works" className="land-nav-link">How it works</a>
          <a href="#pricing" className="land-nav-link">Pricing</a>
          <a href="#faq" className="land-nav-link">FAQ</a>
          <Link to="/login" className="land-btn-signin" style={{ marginLeft: 8 }}>Sign In</Link>
          <Link to="/register" className="land-btn-start" style={{ marginLeft: 8 }}>Get Started</Link>
        </div>
        {/* Mobile hamburger */}
        <button className="land-hamburger" onClick={() => setMobileOpen(o => !o)} aria-label="Menu">
          <span />
          <span />
          <span />
        </button>
      </nav>

      {/* ── Mobile Menu ── */}
      <div className={`land-mobile-menu ${mobileOpen ? 'open' : ''}`}>
        <a href="#features" className="land-mobile-link" onClick={() => setMobileOpen(false)}>Features</a>
        <a href="#how-it-works" className="land-mobile-link" onClick={() => setMobileOpen(false)}>How it works</a>
        <a href="#pricing" className="land-mobile-link" onClick={() => setMobileOpen(false)}>Pricing</a>
        <a href="#faq" className="land-mobile-link" onClick={() => setMobileOpen(false)}>FAQ</a>
        <Link to="/login" className="land-mobile-link" onClick={() => setMobileOpen(false)}>Sign In</Link>
        <Link to="/register" className="land-mobile-link" style={{ color: '#1a5c2a', fontWeight: 700 }} onClick={() => setMobileOpen(false)}>Get Started →</Link>
      </div>

      {/* ── Hero ── */}
      <section className="land-hero">
        <div className="land-hero-badge">
          <span style={{ width: 8, height: 8, background: '#4ade80', borderRadius: '50%', display: 'inline-block' }} />
          30-day free trial · No commitment · Cancel anytime
        </div>

        <h1>
          The Smarter Way to Run<br />
          Your <span>Restaurant Menu</span>
        </h1>

        <p className="land-hero-sub">
          Go digital in minutes. Create your menu, generate a QR code, accept online orders,
          and manage everything from one powerful dashboard.
        </p>

        <div className="land-hero-cta">
          <Link to="/register" className="land-cta-primary">
            Start Today →
          </Link>
          <a href="#how-it-works" className="land-cta-secondary">
            See how it works
          </a>
        </div>

        {/* Product Preview */}
        <div className="land-preview">
          <div className="land-preview-bar">
            <div className="land-preview-dot" style={{ background: '#ff5f57' }} />
            <div className="land-preview-dot" style={{ background: '#febc2e' }} />
            <div className="land-preview-dot" style={{ background: '#28c840' }} />
            <span style={{ marginLeft: 12, fontSize: 12, color: 'rgba(255,255,255,0.5)', fontFamily: 'monospace' }}>getqrdigimenu.com/menu/your-restaurant</span>
          </div>
          <div className="land-preview-inner">
            <div className="land-preview-stat">
              <div className="land-preview-stat-val">48</div>
              <div className="land-preview-stat-label">Menu Items</div>
            </div>
            <div className="land-preview-stat">
              <div className="land-preview-stat-val">12</div>
              <div className="land-preview-stat-label">Orders Today</div>
            </div>
            <div className="land-preview-stat">
              <div className="land-preview-stat-val">12</div>
              <div className="land-preview-stat-label">Categories</div>
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>Menu Items</span>
              <span style={{ fontSize: 12, fontWeight: 700, background: currentCuisine.color, color: 'white', padding: '3px 12px', borderRadius: 50, transition: 'all 0.3s' }}>{currentCuisine.label}</span>
            </div>
            <div className="land-preview-menu" style={{ opacity: fadeIn ? 1 : 0, transition: 'opacity 0.3s ease' }}>
              {currentCuisine.items.map(item => (
                <div key={item.name} className="land-preview-item">
                  <img src={item.img} alt={item.name} style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', flexShrink: 0, marginRight: 10 }} onError={e => e.target.style.display = 'none'} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="land-preview-item-name">{item.name}</div>
                    <span className="land-preview-item-badge" style={{ background: item.tagColor, color: item.tagText }}>{item.tag}</span>
                  </div>
                  <div className="land-preview-item-price">{item.price}</div>
                </div>
              ))}
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', gap: 6, paddingTop: 4 }}>
              {CUISINES.map((c, i) => (
                <div key={i} onClick={() => { setFadeIn(false); setTimeout(() => { setCuisineIdx(i); setFadeIn(true); }, 300); }}
                  style={{ width: 8, height: 8, borderRadius: '50%', background: i === cuisineIdx ? currentCuisine.color : '#ddd', cursor: 'pointer', transition: 'all 0.3s' }} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Live Stats Bar — only show when real data available ── */}
      {showStats && (
        <div style={{ background: '#0f1117', padding: '28px 32px', display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 0 }}>
          {[
            liveStats.total_restaurants > 0 && { num: liveStats.total_restaurants + '+', label: 'Restaurants Live' },
            liveStats.total_scans > 0 && { num: liveStats.total_scans > 999 ? Math.floor(liveStats.total_scans / 1000) + 'K+' : liveStats.total_scans + '+', label: 'Menu Scans' },
            liveStats.total_reactions > 0 && { num: liveStats.total_reactions > 999 ? Math.floor(liveStats.total_reactions / 1000) + 'K+' : liveStats.total_reactions + '+', label: '❤️ Reactions' },
            liveStats.positive_pct > 0 && { num: liveStats.positive_pct + '%', label: '😍 Positive' },
          ].filter(Boolean).map((s, i, arr) => (
            <div key={s.label} style={{ textAlign: 'center', padding: '12px 36px', borderRight: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
              <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 30, fontWeight: 800, color: '#4ade80', lineHeight: 1 }}>{s.num}</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginTop: 6 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Features ── */}
      <div id="features">
        <div className="land-section">
          <div className="land-section-tag">Features</div>
          <h2 className="land-section-title">Everything your restaurant needs</h2>
          <p className="land-section-sub">
            Built specifically for restaurants — from single locations to multi-branch operations.
            No technical skills required.
          </p>
          <div className="land-features">
            {FEATURES.map(f => (
              <div key={f.title} className="land-feature-card">
                <div className="land-feature-icon">{f.icon}</div>
                <div className="land-feature-title">{f.title}</div>
                <div className="land-feature-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── How It Works ── */}
      <div id="how-it-works" className="land-hiw">
        <div className="land-hiw-inner">
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div className="land-section-tag" style={{ textAlign: 'center' }}>How It Works</div>
            <h2 className="land-section-title" style={{ margin: '12px auto 16px', textAlign: 'center' }}>
              Up and running in under 10 minutes
            </h2>
            <p className="land-section-sub" style={{ margin: '0 auto', textAlign: 'center' }}>
              No complicated setup. No technical knowledge needed. Just sign up and start.
            </p>
          </div>
          <div className="land-steps">
            {STEPS.map(s => (
              <div key={s.num} className="land-step">
                <div className="land-step-num">{s.num}</div>
                <div className="land-step-title">{s.title}</div>
                <p className="land-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Live Demo Section ── */}
<div className="land-demo">
  <div className="land-demo-inner">

    {/* Left — copy */}
    <div>
      <div className="land-demo-tag">Live on the Platform</div>
      <h2 className="land-demo-title">
        See a real restaurant<br />using it right now
      </h2>
      <p className="land-demo-sub">
        Persis Biryani & Indian Grill in Valdosta, GA built their entire digital menu on QR Digi Menu. 
        Scan the code or click below to see exactly what your customers will experience.
      </p>
      <div className="land-demo-pills">
        <div className="land-demo-pill">
          <div className="land-demo-pill-icon">📸</div>
          <div className="land-demo-pill-text">
            <strong>Real food photos</strong> on every item — customers know exactly what they're ordering before they tap
          </div>
        </div>
        <div className="land-demo-pill">
          <div className="land-demo-pill-icon">🌶️</div>
          <div className="land-demo-pill-text">
            <strong>1–7 spice scale</strong> per dish — no more "how spicy is this?" questions at the counter
          </div>
        </div>
        <div className="land-demo-pill">
          <div className="land-demo-pill-icon">🌍</div>
          <div className="land-demo-pill-text">
            <strong>15-language translation</strong> — one tap and the entire menu switches language instantly
          </div>
        </div>
        <div className="land-demo-pill">
          <div className="land-demo-pill-icon">🥗</div>
          <div className="land-demo-pill-text">
            <strong>Veg / Non-veg / Vegan filters</strong> — diners find what they can eat in seconds
      </div>
        </div>
      </div>
      <a
        href="https://getqrdigimenu.com/menu/persis-biryani-indian-grill"
        target="_blank"
        rel="noreferrer"
        className="land-demo-view-btn"
      >
        View Live Menu ↗
      </a>
    </div>

    {/* Right — QR card */}
    <div className="land-demo-right">
      <div className="land-demo-live-dot">Live Restaurant</div>
      <div className="land-demo-qr-card">
        <div className="land-demo-qr-label">SCAN TO EXPERIENCE IT</div>
        <QRCodeSVG
          value="https://getqrdigimenu.com/menu/persis-biryani-indian-grill"
          size={200}
          level="H"
          fgColor="#0f1117"
          bgColor="#ffffff"
        />
        <div className="land-demo-qr-restaurant">Persis Biryani & Indian Grill</div>
        <div className="land-demo-qr-cuisine">Valdosta, GA · Indian Cuisine</div>
        <div className="land-demo-qr-divider" />
        <div className="land-demo-features-row">
          <div className="land-demo-feat">
            <span className="land-demo-feat-icon">📸</span>
            Photos
          </div>
          <div className="land-demo-feat">
            <span className="land-demo-feat-icon">🌶️</span>
            Spice Scale
          </div>
          <div className="land-demo-feat">
            <span className="land-demo-feat-icon">🌍</span>
            15 Languages
          </div>
          <div className="land-demo-feat">
            <span className="land-demo-feat-icon">🛒</span>
            Ordering
          </div>
        </div>
      </div>
      <div className="land-demo-mobile-hint">Point your phone camera at the QR code</div>
    </div>

  </div>
</div>

      {/* ── Pricing ── */}
      <div id="pricing">
        <div className="land-section" style={{ textAlign: 'center' }}>
          <div className="land-section-tag">Pricing</div>
          <h2 className="land-section-title" style={{ margin: '12px auto 16px' }}>Simple, honest pricing</h2>
          <p className="land-section-sub" style={{ margin: '0 auto 32px' }}>
            Flat monthly fee. No commissions. No hidden fees. Ever.
          </p>
          {/* Monthly / Annual Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 40 }}>
            <span style={{ fontSize: 14, color: pricingPeriod === 'monthly' ? '#1a5c2a' : '#999', fontWeight: pricingPeriod === 'monthly' ? 700 : 400 }}>Monthly</span>
            <div onClick={() => setPricingPeriod(p => p === 'monthly' ? 'annual' : 'monthly')}
              style={{ width: 48, height: 26, borderRadius: 20, cursor: 'pointer', background: pricingPeriod === 'annual' ? '#1a5c2a' : '#ccc', position: 'relative', transition: 'background 0.2s' }}>
              <div style={{ position: 'absolute', top: 3, left: pricingPeriod === 'annual' ? 25 : 3, width: 20, height: 20, borderRadius: '50%', background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </div>
            <span style={{ fontSize: 14, color: pricingPeriod === 'annual' ? '#1a5c2a' : '#999', fontWeight: pricingPeriod === 'annual' ? 700 : 400 }}>
              Annual <span style={{ background: '#dcfce7', color: '#16a34a', fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 700, marginLeft: 4 }}>SAVE 2 MONTHS</span>
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(300px, 1fr))', maxWidth: 1200, margin: '0 auto', gap: '24px' }} className="pricing-grid-responsive">
            <style>{`
              @media (max-width: 768px) {
               .land-pricing-grid { grid-template-columns: 1fr !important; }
              }
            `}</style>
            {/* Free Plan */}
            <div className="land-price-card">
              <div className="land-price-name">Free</div>
              <div className="land-price-amount">$0</div>
              <div className="land-price-period">forever</div>
              <ul className="land-price-features">
                {[
                  '1 Restaurant',
                  'Unlimited menu items',
                  'QR Code generation',
                  'Basic digital menu with photos',
                  'Gallery & photos',
                  'Opening hours display',
                  'Menu filters (Veg/Non-veg/Vegan)',
                  'Spice level display',
                  '10 scans/day · 35 scans/week',
                ].map(f => (
                  <li key={f}><span style={{ color: '#1a5c2a' }}>✓</span> {f}</li>
                ))}
                <li style={{ color: '#bbb' }}><span>✗</span> <span style={{ textDecoration: 'line-through' }}>15-language translation</span></li>
                <li style={{ color: '#bbb' }}><span>✗</span> <span style={{ textDecoration: 'line-through' }}>Google Maps link</span></li>
                <li style={{ color: '#bbb' }}><span>✗</span> <span style={{ textDecoration: 'line-through' }}>Social media links</span></li>
                <li style={{ color: '#bbb' }}><span>✗</span> <span style={{ textDecoration: 'line-through' }}>Online ordering</span></li>
              </ul>
              <Link to="/register" className="land-price-btn outline">Get Started Free</Link>
              <p style={{ fontSize: 11, color: '#888', marginTop: 8 }}>No credit card required</p>
            </div>
            {/* Basic Plan */}
            <div className="land-price-card">
              <div className="land-price-name">Basic</div>
              {pricingPeriod === 'monthly' ? (
                <><div className="land-price-amount">$19<span style={{ fontSize: 20 }}>.99</span></div><div className="land-price-period">per month</div></>
              ) : (
                <><div className="land-price-amount">$199</div><div className="land-price-period">per year</div><div style={{ fontSize: 13, color: '#16a34a', fontWeight: 600, marginBottom: 4 }}>$16.58/mo — save $40</div></>
              )}
              <ul className="land-price-features">
                {['1 Restaurant', 'Unlimited menu items', 'QR Code generation', 'Beautiful digital menu with photos', 'Gallery & photos', 'Opening hours display', 'Menu filters (Veg/Non-veg/Vegan)', '15-language translation', 'Spice level display (1–7 scale)', 'Unlimited QR menu scans', '📍 Google Maps link', '📸 Social media links', 'Email support'].map(f => (
                  <li key={f}><span style={{ color: '#1a5c2a' }}>✓</span> {f}</li>
                ))}
                <li style={{ color: '#bbb' }}><span>✗</span> <span style={{ textDecoration: 'line-through' }}>Online ordering</span></li>
                <li style={{ color: '#bbb' }}><span>✗</span> <span style={{ textDecoration: 'line-through' }}>Catering requests</span></li>
              </ul>
              <Link to="/register" className="land-price-btn outline">Start Free Trial</Link>
              <p style={{ fontSize: 11, color: '#16a34a', marginTop: 8, fontWeight: 600 }}>✓ 30-day free Pro trial · No card required</p>
              {pricingPeriod === 'annual' && <p style={{ fontSize: 11, color: '#999', marginTop: 4 }}>Billed as $199/year after trial</p>}
            </div>
            {/* Pro Plan */}
            <div className="land-price-card featured">
              <div className="land-price-badge">Most Popular</div>
              <div className="land-price-name" style={{ color: 'white' }}>Pro</div>
              {pricingPeriod === 'monthly' ? (
                <><div className="land-price-amount" style={{ color: '#4ade80' }}>$39<span style={{ fontSize: 20 }}>.99</span></div><div className="land-price-period">per month</div></>
              ) : (
                <><div className="land-price-amount" style={{ color: '#4ade80' }}>$399</div><div className="land-price-period">per year</div><div style={{ fontSize: 13, color: '#4ade80', fontWeight: 600, marginBottom: 4 }}>$33.25/mo — save $80</div></>
              )}
              <ul className="land-price-features">
                {['Everything in Basic', '🛒 Online ordering system', '📧 Auto order confirmation emails', '🔔 Real-time order notifications', '🍽️ Catering request management', '📊 Order & scan analytics', 'Priority support'].map(f => (
                  <li key={f} style={{ color: 'rgba(255,255,255,0.85)', borderBottomColor: 'rgba(255,255,255,0.08)' }}>
                    <span style={{ color: '#4ade80' }}>✓</span> {f}
                  </li>
                ))}
              </ul>
              <Link to="/register" className="land-price-btn filled">Start Free Trial</Link>
              <p style={{ fontSize: 11, color: '#4ade80', marginTop: 8, fontWeight: 600 }}>✓ 30-day free Pro trial · No card required</p>
              {pricingPeriod === 'annual' && <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Billed as $399/year after trial</p>}
            </div>
          </div>
          <p style={{ marginTop: 24, fontSize: 13, color: '#999' }}>
            🎁 30-day free Pro trial · No card required · Cancel anytime · Powered by Lemon Squeezy (Stripe)
          </p>
        </div>
      </div>

      {/* ── FAQ ── */}
      <div id="faq" className="land-faq">
        <div className="land-faq-inner">
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div className="land-section-tag" style={{ textAlign: 'center' }}>FAQ</div>
            <h2 className="land-section-title" style={{ margin: '12px auto 16px', textAlign: 'center' }}>
              Questions? We have answers.
            </h2>
            <p className="land-section-sub" style={{ margin: '0 auto', textAlign: 'center' }}>
              Everything restaurant owners ask before getting started.
            </p>
          </div>
          {FAQS.map((faq, i) => (
            <div key={i} className={`land-faq-item ${openFaq === i ? 'open' : ''}`}>
              <div className="land-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                <span>{faq.q}</span>
                <span className="land-faq-icon">+</span>
              </div>
              <div className="land-faq-a">{faq.a}</div>
            </div>
          ))}
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <p style={{ fontSize: 14, color: '#666' }}>
              Still have questions?{' '}
              <a href="mailto:hello@getqrdigimenu.com" style={{ color: '#1a5c2a', fontWeight: 600, textDecoration: 'none' }}>
                Email us →
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* ── Final CTA ── */}
      <section className="land-cta-section">
        <h2 className="land-cta-title">Ready to go digital?</h2>
        <p className="land-cta-sub">
          Start your 30-day free trial today.<br />
          No commitment. Cancel anytime.
        </p>
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
          <Link to="/register" className="land-cta-primary" style={{ fontSize: 17, padding: '16px 40px' }}>
            Create Your Menu →
          </Link>
          <Link to="/login" className="land-cta-secondary" style={{ fontSize: 17, padding: '16px 32px' }}>
            Sign In
          </Link>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="land-footer">
        <div className="land-footer-inner">
          <div>
            <div className="land-footer-brand">
              <img src={require('../assets/logo.png')} alt="QR Digi Menu" style={{ width: 36, height: 36, borderRadius: 10, objectFit: 'cover' }} />
              QR Digi Menu
            </div>
            <div className="land-footer-tagline">The complete digital menu platform for modern restaurants.</div>
          </div>
          <div>
            <div className="land-footer-col-title">Product</div>
            <a href="#features" className="land-footer-link">Features</a>
            <a href="#pricing" className="land-footer-link">Pricing</a>
            <a href="#how-it-works" className="land-footer-link">How it works</a>
            <a href="#faq" className="land-footer-link">FAQ</a>
          </div>
          <div>
            <div className="land-footer-col-title">Account</div>
            <Link to="/register" className="land-footer-link">Get Started</Link>
            <Link to="/login" className="land-footer-link">Sign In</Link>
            <Link to="/forgot-password" className="land-footer-link">Reset Password</Link>
          </div>
          <div>
            <div className="land-footer-col-title">Support</div>
            <a href="mailto:hello@getqrdigimenu.com" className="land-footer-link">Contact Us</a>
            <Link to="/privacy" className="land-footer-link">Privacy Policy</Link>
            <Link to="/terms" className="land-footer-link">Terms of Service</Link>
            <Link to="/refund" className="land-footer-link">Refund Policy</Link>
          </div>
        </div>
        <div className="land-footer-bottom">
          <span>© {new Date().getFullYear()} QR Digi Menu Inc. All rights reserved.</span>
          <span>Built for restaurants worldwide 🌍</span>
        </div>
      </footer>
    </div>
  );
}